// Wave 16 — HUD chip for the live session showing meter status.
//
// Reads from useSubscription().usage (populated live by the heartbeat
// piggyback). Hidden entirely on rapha.aero.chat / role='rapha'.

import React from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSubscription } from '../services/useSubscription';
import { isRaphaDomain } from '../services/subdomain';
import { readCurrentUser } from '../services/useCurrentUser';
import { colors } from '../theme';

export default function UsageChip() {
  const { usage } = useSubscription();
  if (isRaphaDomain()) return null;
  if (readCurrentUser().role === 'rapha') return null;
  if (!usage) return null;

  let label = '';
  let bgTint = `${colors.bg}CC`;
  let fg = colors.textMuted;

  if (usage.isFree) {
    const leftMin = Math.max(
      0,
      Math.round(((usage.weeklyLimitSec ?? 0) - usage.weeklyUsedSec) / 60),
    );
    label = `${leftMin} min left this week`;
    if (leftMin === 0) {
      fg = colors.danger;
    } else if (leftMin <= 5) {
      fg = colors.warning;
    } else {
      fg = colors.brand;
    }
  } else {
    const overageMin = Math.round(usage.todayOverageSec / 60);
    if (overageMin > 0) {
      const dollars = ((overageMin * 20) / 100).toFixed(2);
      label = `overage: ${overageMin} min ($${dollars})`;
      fg = colors.warning;
    } else {
      const usedMin = Math.round(usage.dailyUsedSec / 60);
      const included = Math.round((usage.dailyIncludedSec ?? 0) / 60);
      label = `${usedMin} / ${included} min today`;
      fg = colors.brand;
    }
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        backgroundColor: bgTint,
        border: `1px solid ${colors.border}`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <AccessTimeIcon sx={{ fontSize: 14, color: fg }} />
      <Typography variant="caption" sx={{ color: fg, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}
